import React, {
  memo,
  useEffect,
  useState,
  useContext,
  useRef,
  useMemo,
} from 'react';
import moment from 'moment';
import {
  PWCInput,
  PWCSelect,
  PWCDatePicker,
} from '@components/Form/components';

import { EditableContext } from '../index';
import { EDIT_TABLE_COLUMNS_TYPE } from '@utils/enums';
import { isObject } from '@utils/common';
import { isFunction } from 'lodash';
import { handleMomentData } from '../dataProcessors';
import GanttCombCell from './GanttCombCell';
import GanttDurationCell from './GanttDurationCell';
import '../index.scss';

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex: sourceDataIndex,
  required, // 是否是必填的
  type, // 标记是什么类型，如：Input、Select、RichEditor
  compProps = {}, // 每一项可编辑内容的props
  alwaysEditMode = false, // 一直是编辑模式
  disabled = false, // 是否禁用
  placeholder, // 没有值的时候默认显示的文案，与输入框选择框的placeholder不是一个性质
  record,
  handleSave,
  handleAdd,
  isEditMode,
  changeData,
  tableDataSource,
  ...restProps
}) => {
  const dataIndex = `${sourceDataIndex}_${record?.id}`;
  // 日期类型
  const isDatePicker = type === EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER;
  // console.log('record: ', record);
  // console.log('title: ', title);
  // console.log('dataIndex: ', dataIndex);
  // console.log('restProps: ', restProps);
  // console.log('type: ', type);
  // console.log('compProps: ', compProps);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing && inputRef?.current) {
      inputRef.current.focus();
    }
  }, [editing, inputRef]);

  const handleFormData = () => {
    if (record) {
      form.setFieldsValue({
        [dataIndex]:
          isDatePicker && record[dataIndex]
            ? moment(record[dataIndex])
            : record[dataIndex],
        [sourceDataIndex]:
          isDatePicker && record[dataIndex]
            ? moment(record[dataIndex])
            : record[dataIndex],
      });
    }
  };

  // 进入编辑模式时初始化form数据
  useEffect(() => {
    handleFormData();
  }, [alwaysEditMode, record]);

  // 切换编辑模式
  const toggleEdit = () => {
    if (disabled) {
      return;
    }
    setEditing(!editing);
    handleFormData();
  };

  // 失焦、选择调用
  const save = async (
    { needRefreshTableData } = {
      needRefreshTableData: false,
    },
  ) => {
    const values = await form.getFieldsValue();
    if (!alwaysEditMode) {
      toggleEdit();
    }
    // 单独处理日期的值
    Object.keys(values).map((key) => {
      handleMomentData(values[key]);
    });
    const resValue = {
      ...record,
      ...values,
    };
    handleSave(resValue, needRefreshTableData);
  };

  const onChangeForDatePicker = async () => {
    await save();
    await form?.validateFields();
  };

  // 处理不是 react node 的 children
  if (Array.isArray(children) && isObject(children[1])) {
    children = children.join('');
  }
  let childNode = children;

  const validateSpaceAndEnter = (label) => () => ({
    validator(_, value) {
      if (!value || !value.trim()) {
        return Promise.reject(`${label} is required`);
      }
      return Promise.resolve();
    },
  });

  // 组件的参数
  const formItemInfo = useMemo(() => {
    // 处理rules-如果required为true那么自动追加一个required判断条件
    const rules = compProps.rules || [];
    if (
      (isFunction(required) ? required(record) : required) &&
      !rules.some((r) => r == validateSpaceAndEnter(title)) &&
      type === EDIT_TABLE_COLUMNS_TYPE.INPUT
    ) {
      rules.push(validateSpaceAndEnter(title));
    }

    if (
      (isFunction(required) ? required(record) : required) &&
      !rules.some((r) => r.message == `${title} is required.`) &&
      type !== EDIT_TABLE_COLUMNS_TYPE.INPUT
    ) {
      rules.push({
        required: true,
        message: `${title} is required.`,
      });
    }

    // 处理rules-END
    const formItem = {
      name: dataIndex,
      rules,
      style: { margin: 0 },
      formItemStyle: { margin: 0 },
      onChangeForDatePicker,
      record,
      handleAdd,
      isEditMode,
      compProps: {
        ...compProps,
        disabled: isFunction(compProps?.disabled)
          ? compProps.disabled(record)
          : disabled,
        ref: inputRef,
        // onChange: save, // 目前的设计千万别监听change事件，否则输入框那里输入一下就会失焦一下，因为更改了数据
        onBlur: save,
      },
    };
    // console.log('最终的dataIndex: ', dataIndex);
    // console.log('aaaaaa: ', formItem.name);

    if (
      [
        EDIT_TABLE_COLUMNS_TYPE.SELECT,
        EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER,
      ].includes(type)
    ) {
      formItem.compProps.onSelect = () => save({ needRefreshTableData: true });
    }
    return formItem;
  }, []);

  if (editable) {
    // 默认显示的默认值
    const renderDefaultConteng = placeholder;
    childNode =
      editing || alwaysEditMode ? (
        {
          [EDIT_TABLE_COLUMNS_TYPE.INPUT]: <PWCInput {...formItemInfo} />,
          [EDIT_TABLE_COLUMNS_TYPE.SELECT]: <PWCSelect {...formItemInfo} />,
          [EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER]: (
            <PWCDatePicker {...formItemInfo} />
          ),
          [EDIT_TABLE_COLUMNS_TYPE.GanttCombCell]: (
            <GanttCombCell {...formItemInfo} />
          ),
        }[type]
      ) : (
        <div
          className="editable-cell-value-wrap"
          // onClick={toggleEdit}
          // dangerouslySetInnerHTML={{
          //   __html: children || renderDefaultConteng,
          // }}
        >
          {children || renderDefaultConteng}
        </div>
      );
    if (type === EDIT_TABLE_COLUMNS_TYPE.GanttDurationCell) {
      childNode = (
        <GanttDurationCell
          tableDataSource={tableDataSource}
          {...formItemInfo}
        />
      );
    }
  }
  return <td {...restProps}>{childNode}</td>;
};

export default memo(EditableCell);
